import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ETLBasefeed, ETLReportingSetObj, ReportingSetSections, RowDataObj, PERMISSION } from '../../cxone-etl-reporting-set.model';
import { CxoneEtlReportingSetService } from '../../cxone-etl-reporting-set.service';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AddSimpleFeedModalComponent } from '../add-simple-feed-modal/add-simple-feed-modal.component';
import { CellClickedEvent, GridOptions, GridApi, GridReadyEvent } from 'ag-grid-community';
import { DeleteRowRenderer } from '../delete-row-renderer/delete-row-renderer.component';

@Component({
    selector: "cv-cxone-reporting-set-create-edit",
    templateUrl: "./cxone-reporting-set-create-edit.component.html",
    styleUrls: ["./cxone-reporting-set-create-edit.component.scss"],
    providers: [DialogService],
})
export class CxoneReportingSetCreateEditComponent implements OnInit {
    @Input() editReportingSetData: ETLReportingSetObj;
    @Input() permissions: string[];
    @Output() public sectionChange = new EventEmitter<ReportingSetSections>();
    public reportingSetSections = ReportingSetSections;
    public reportingSetForm: FormGroup;
    public reportingSetData: ETLReportingSetObj[];
    public feednamesList: ETLBasefeed[] = [];
    public descriptionCharCount = 0;
    public reportingSetNameExist = false;
    public basefeedOptions: ETLBasefeed[];
    public categoryOptions: ETLBasefeed[] = [];
    public enterReportingSetName: string;
    public reportDescription: string;
    public modalRef: DynamicDialogRef = null;
    public rowData: RowDataObj[] = [];
    public showGrid = false;
    public gridOptions: GridOptions;
    public showX = false;
    public reportNameExist = false;
    public showFlowBuilder: boolean;
    public reportingSetPageTitle = "";
    public saveBtnVisible = false;
    public dropdownPlaceholderText = '';
    public reportingSetDef: any;
    public showEditReportingSet: boolean;
    public showSpinner: boolean;
    public gridApi: GridApi;
    public editForm = false;
    public simpleFieldsExists = false;
    public reportingSetName = "";
    public basefeed = "";
    public canEditLocked = false;
    public canEdit = false;

    constructor(
        private cvLocalizationService: CVLocalizationService,
        public cxoneEtlReportingSet: CxoneEtlReportingSetService,
        private toasterManager: ToastrManager,
        private dialogSrvc: DialogService
    ) { }

    public async ngOnInit(): Promise<void> {
        this.canEditLocked = this.permissions?.includes(PERMISSION.ADMIN);
        this.canEdit = this.permissions?.includes(PERMISSION.EDIT);
        this.showSpinner = true;
        this.showEditReportingSet = false;
        this.initializeReportingSetForm();
        await this.loadLocalization();
        this.loadReportingSetData();
        await this.loadBasefeedData();
        if (this.editReportingSetData) {
            this.setFetchedFormValues();
            this.setAndDisableReportingSetName();
            this.saveBtnVisible = true;
            this.editForm = true;
        }
        this.showSpinner = false;
        this.showEditReportingSet = true;

        this.reportingSetForm?.valueChanges.subscribe(() => {
            sendMessageCXOne({hasNonSavedChanges: true});
        });
    }

    public loadReportingSetData() {
        this.reportingSetData = [];
        this.categoryOptions = [];
        try {
            this.reportingSetData = this.cxoneEtlReportingSet.getReportingSet();
            const uniqueCategories = [...new Set(this.reportingSetData.map(item => item.ReportingSetGroup))];
            this.categoryOptions = uniqueCategories.map((name) => ({ id: name, name }));
        }
        catch (error) {
            this.toasterManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
    }

    public async loadBasefeedData() {
        this.basefeedOptions = [];
        try {
            this.feednamesList = await this.cxoneEtlReportingSet.loadBasefeed();
        }
        catch (error) {
            this.toasterManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
        finally {
            this.basefeedOptions = [...this.feednamesList];
        }
    }

    public initializeReportingSetForm() {
        this.reportingSetForm = new FormGroup({
            reportingSetName: new FormControl('', [Validators.required, Validators.maxLength(100), this.forbiddenCharactersValidator]),
            description: new FormControl('', Validators.maxLength(1000)),
            category: new FormControl('', Validators.required),
            basefeed: new FormControl('', Validators.required)
        });
    }

    public setFetchedFormValues() {
        const fetchedReportingSetName =
            this.editReportingSetData.ReportingSetName || "";
        const fetchedDescription =
            this.editReportingSetData.ReportingSetDescription || "";
        const fetchedCategory = this.editReportingSetData.ReportingSetGroup || "";
        const fetchedBasefeed = this.editReportingSetData.BaseFeed || "";

        const DataList = this.basefeedOptions.find(item => item.FeedName === fetchedBasefeed);
        this.reportingSetForm.setValue({
            reportingSetName: fetchedReportingSetName,
            description: fetchedDescription,
            category: { name: fetchedCategory, id: fetchedCategory },
            basefeed: { FeedName: fetchedBasefeed, _id: fetchedBasefeed, ...DataList },
        });
        this.reportingSetForm.updateValueAndValidity();
        this.descriptionFieldCharCount();
        this.setFetchedBaseFields();
    }

    public setFetchedBaseFields() {
        const dataFields = this.editReportingSetData.DataFields;
        const baseFields = [];
        for (const fieldKey in dataFields) {
            if (dataFields.hasOwnProperty(fieldKey)) {
                const fetchedBaseFields = { FIELDS: "", "DATA TYPE": "" };
                if (dataFields[fieldKey].hasOwnProperty("Field")) {
                    fetchedBaseFields["FIELDS"] = dataFields[fieldKey]["Field"];
                    fetchedBaseFields["DATA TYPE"] = this.getDataType(dataFields[fieldKey]["ExpectedType"]);
                    baseFields.push(fetchedBaseFields);
                }
            }
        }
        this.rowData = baseFields;
        if (this.rowData && this.rowData.length > 0) {
            this.simpleFieldsExists = true;
            this.showGrid = true;
        } else {
            this.showGrid = false;
        }
        this.setGridOption();
    }

    getDataType(dataType: number): string {
        let convertedDataType = "";
        switch (dataType) {
            case 0:
                convertedDataType = "String";
                break;
            case 1:
                convertedDataType = "Integer";
                break;
            case 2:
                convertedDataType = "Floating-Point";
                break;
            case 3:
                convertedDataType = "Date";
                break;
            case 4:
                convertedDataType = "Boolean";
                break;
        }
        return convertedDataType;
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection("ETL_Reporting_Set");
        } catch {
            console.log("Failed to load localization for ETL_Reporting_Set");
        } finally {
            this.enterReportingSetName = this.getLocalizationString(
                "Enter_Reporting_Set_Name",
                "Enter reporting set name"
            );
            this.reportDescription = this.getLocalizationString(
                "Add_Reporting_Description",
                "Add a reporting set description"
            );
            this.reportingSetPageTitle = this.initializeTitle();
            this.dropdownPlaceholderText = this.getLocalizationString("Select_Item", "Select...");
        }
    }

    public initializeTitle() {
        let key = "";
        let defaultValue = "";
        if (!this.editReportingSetData) {
            key = "Create_Reporting_Set";
            defaultValue = "Create Reporting Set";
        } else {
            key = "Edit_Reporting_Set";
            defaultValue = "Edit Reporting Set";
        }
        return this.getLocalizationString(key, defaultValue);
    }

    public reportingSetNameDuplicateValidation(Checkname) {
        if (Checkname.target?.value) {
            const reportingSetKey = Checkname.target.value.toUpperCase().trim();
            this.reportingSetNameExist = this.reportingSetData.some(obj => obj.ReportingSetName.toUpperCase() === reportingSetKey);
            this.reportNameExist = Checkname.target.value !== "";
        }
    }

    public forbiddenCharactersValidator(control: FormControl) {
        const forbidden = /[\^\/\\\!\+\<\>\?\#\&\@\"\,]/.test(control.value);
        const constainsEmoji = /[^\u0000-\u007F]/.test(control.value);
        return forbidden || constainsEmoji ? { forbiddenCharacters: { value: control.value } } : null;
    }

    public get charLimitReportNameError() {
        return this.reportingSetForm.controls['reportingSetName'].errors?.maxlength && !this.forbiddenCharError;
    }

    public get forbiddenCharError() {
        return this.reportingSetForm.controls['reportingSetName'].errors?.forbiddenCharacters && !this.charLimitReportNameError;
    }

    public get charLimitDescriptionError() {
        return this.reportingSetForm.controls['description'].errors?.maxlength;
    }

    public descriptionFieldCharCount() {
        this.descriptionCharCount = this.reportingSetForm.get('description').value.length;
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText(
            "ETL_Reporting_Set",
            key,
            defaultValue
        );
        return baseString;
    }

    public showSimpleFieldsModal() {
        const selectedBaseFeed = this.reportingSetForm.get('basefeed').value?.FeedName;
        this.modalRef = this.dialogSrvc.open(AddSimpleFeedModalComponent, {
            width: "756px",
            height: "553px",
            data: {
                baseFeed: selectedBaseFeed,
            },
        });
        this.modalRef.onClose.subscribe((value) => {
            if (value && value.length > 0) {
                if (this.rowData.length > 0) {
                    this.rowData = [...this.rowData, ...this.handleDuplicates(value)];
                    this.gridApi.setRowData(this.rowData);
                } else {
                    this.rowData = value;
                    this.showGrid = true;
                    this.setGridOption();
                }
            } else {
                this.showGrid = false;
            }
        });
    }

    public changeSection(section: number): void {
        if (section === 0) {
            this.sectionChange.emit(section);
            sendMessageCXOne({hasNonSavedChanges: false});
        } 
        else this.navigateToEditReportingSet();
    }

    public showHideFlowBuilder(isShow: boolean) {
        this.showFlowBuilder = isShow;
        if (this.editReportingSetData) {
            let editReportingSetData = { 'editReportingSetData': this.editReportingSetData };
            let DataList = this.basefeedOptions.find(item => item.FeedName === this.basefeed);
            let fetchedBasefeed = { FeedName: this.basefeed, _id: this.basefeed, ...DataList };
            this.reportingSetDef = { ...this.reportingSetForm.value, ...editReportingSetData, reportingSetName: this.reportingSetName, basefeed: fetchedBasefeed };
        } else {
            this.reportingSetDef = { ...this.reportingSetForm.value };
        }
    }

    onCreate(e) {
        if (!this.checkForEmpty(e.text)) {
            const newId = this.categoryOptions.length; // Generate new ID
            const value = { id: e.text, name: e.text };
            this.categoryOptions.push(value); // Push the new item to the array
            this.categoryOptions = [...this.categoryOptions];
            this.reportingSetForm.get("category").patchValue(value);
        }
    }

    onCellClicked(event: CellClickedEvent) {
        this.rowData = this.rowData.filter((_, index) => index !== event.rowIndex);
        this.gridApi.setRowData(this.rowData);
        if (!this.rowData.length) {
            this.showGrid = false;
        }
    }

    saveReportingSet() {
        this.toasterManager
            .showToaster('success',
                this.getLocalizationString('Save_Reporting_Set', 'Reporting set updated successfully.'));
    }

    navigateToEditReportingSet() {
        const key = "Edit_Reporting_Set";
        const defaultValue = "Edit Reporting Set";
        this.reportingSetPageTitle = this.getLocalizationString(key, defaultValue);
        this.saveBtnVisible = true;
        this.showFlowBuilder = false;
    }

    public checkForEmpty(attributeValue: string | number) {
        return attributeValue == undefined || attributeValue === '' ? true : false;
    }

    async setGridOption() {
        this.gridOptions = {
            columnDefs: [
                {
                    field: "FIELDS",
                    flex: 2,
                    suppressMovable: true
                },
                {
                    field: "DATA TYPE",
                    flex: 1,
                    suppressMovable: true
                },
                {
                    headerName: '',
                    cellRenderer: 'deleteRowRenderer',
                    onCellClicked: (event: CellClickedEvent) => this.onCellClicked(event),
                    cellStyle: { textAlign: 'end' },
                    flex: 1,
                    suppressMovable: true
                }
            ],
            onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
            components: {
                deleteRowRenderer: DeleteRowRenderer
            },
            scrollbarWidth: 5
        };
    }

    async onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridApi.setGridOption('columnDefs', this.gridOptions.columnDefs);
        this.gridApi.setGridOption('rowData', this.rowData);
        this.gridApi.sizeColumnsToFit();
    }

    handleDuplicates(data: any) {
        return data.filter((item: any) => !(this.rowData.map((value: any) => value['FIELDS']).includes(item['FIELDS'])));
    }

    setAndDisableReportingSetName() {
        this.reportingSetName = this.editReportingSetData.ReportingSetName || "";
        this.basefeed = this.editReportingSetData.BaseFeed || "";
        this.reportingSetForm.get('reportingSetName').disable();
        this.reportingSetForm.get('basefeed').disable();

        if (!this.canEdit || (!this.cxoneEtlReportingSet?.getReportingSetData()?.IsEditable && !this.canEditLocked)) {
            this.reportingSetForm.get('description').disable();
            this.reportingSetForm.get('category').disable();
        }
    }
}
